/**
 * A small SVG line chart for MD time series (one line per copy). BMaps has no
 * charting library; this draws axes with "nice" ticks, the lines and a legend,
 * and scales to its container's width. Text uses currentColor, so it follows
 * the color scheme.
 */

export const MD_SERIES_COLORS = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f',
];

/**
 * Round tick values covering [min, max]: steps of 1, 2 or 5 × 10^n.
 * @param {number} min
 * @param {number} max
 * @param {number} [count] - about how many ticks
 * @returns {number[]}
 */
export function niceTicks(min, max, count=5) {
    if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
    if (min === max) {
        const pad = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
        return niceTicks(min - pad, max + pad, count);
    }
    const raw = (max - min) / Math.max(count - 1, 1);
    const mag = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) || 10 * mag;
    const start = Math.floor(min / step) * step;
    const ticks = [];
    for (let v = start; v <= max + step * 0.5; v += step) {
        ticks.push(Number(v.toPrecision(12)));
    }
    return ticks;
}

function formatTick(v) {
    const a = Math.abs(v);
    if (a !== 0 && (a >= 1e5 || a < 1e-2)) return v.toExponential(1);
    return String(Number(v.toPrecision(6)));
}

/**
 * @param {object} props
 * @param {{ name: string, points: Array<[number, number|null]> }[]} props.series
 * @param {string} [props.xLabel]
 * @param {string} [props.yLabel]
 * @param {number} [props.height] - in viewBox units (the width is 600)
 * @param {string} [props.ariaLabel]
 */
export default function MdLineChart({
    series, xLabel, yLabel, height=200, ariaLabel,
}) {
    const width = 600;
    const margin = {
        left: 58, right: 12, top: 10, bottom: 38,
    };
    const finite = ([x, y]) => Number.isFinite(x) && Number.isFinite(y);
    const all = series.flatMap((s) => s.points.filter(finite));
    if (all.length === 0) {
        return <div style={{ fontStyle: 'italic', margin: '0.5rem 0' }}>No data to plot.</div>;
    }
    const xs = all.map(([x]) => x);
    const ys = all.map(([, y]) => y);
    const xTicks = niceTicks(Math.min(...xs), Math.max(...xs), 6);
    const yTicks = niceTicks(Math.min(...ys), Math.max(...ys), 5);
    const [x0, x1] = [Math.min(xTicks[0], ...xs), Math.max(xTicks[xTicks.length - 1], ...xs)];
    const [y0, y1] = [Math.min(yTicks[0], ...ys), Math.max(yTicks[yTicks.length - 1], ...ys)];
    const plotW = width - margin.left - margin.right;
    const plotH = height - margin.top - margin.bottom;
    const px = (x) => margin.left + ((x - x0) / (x1 - x0 || 1)) * plotW;
    const py = (y) => margin.top + plotH - ((y - y0) / (y1 - y0 || 1)) * plotH;

    return (
        <div style={{ width: '100%' }}>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                width="100%"
                role="img"
                aria-label={ariaLabel || yLabel}
                style={{ display: 'block', fontSize: '11px' }}
            >
                {yTicks.filter((t) => t >= y0 && t <= y1).map((t) => (
                    <g key={`y${t}`}>
                        <line x1={margin.left} x2={width - margin.right} y1={py(t)} y2={py(t)} stroke="currentColor" strokeOpacity="0.12" />
                        <text x={margin.left - 6} y={py(t)} textAnchor="end" dominantBaseline="middle" fill="currentColor">{formatTick(t)}</text>
                    </g>
                ))}
                {xTicks.filter((t) => t >= x0 && t <= x1).map((t) => (
                    <g key={`x${t}`}>
                        <line x1={px(t)} x2={px(t)} y1={margin.top + plotH} y2={margin.top + plotH + 4} stroke="currentColor" strokeOpacity="0.5" />
                        <text x={px(t)} y={margin.top + plotH + 16} textAnchor="middle" fill="currentColor">{formatTick(t)}</text>
                    </g>
                ))}
                <line x1={margin.left} x2={width - margin.right} y1={margin.top + plotH} y2={margin.top + plotH} stroke="currentColor" strokeOpacity="0.5" />
                <line x1={margin.left} x2={margin.left} y1={margin.top} y2={margin.top + plotH} stroke="currentColor" strokeOpacity="0.5" />
                {series.map((s, i) => {
                    const pts = s.points
                        .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y))
                        .map(([x, y]) => `${px(x).toFixed(1)},${py(y).toFixed(1)}`)
                        .join(' ');
                    return (
                        <polyline
                            key={s.name}
                            points={pts}
                            fill="none"
                            stroke={MD_SERIES_COLORS[i % MD_SERIES_COLORS.length]}
                            strokeWidth="1.5"
                        >
                            <title>{s.name}</title>
                        </polyline>
                    );
                })}
                {xLabel && (
                    <text x={margin.left + plotW / 2} y={height - 4} textAnchor="middle" fill="currentColor">{xLabel}</text>
                )}
                {yLabel && (
                    <text
                        x={12}
                        y={margin.top + plotH / 2}
                        textAnchor="middle"
                        fill="currentColor"
                        transform={`rotate(-90 12 ${margin.top + plotH / 2})`}
                    >
                        {yLabel}
                    </text>
                )}
            </svg>
            {series.length > 1 && (
                <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1rem', fontSize: '0.8rem', marginTop: '0.1rem',
                }}
                >
                    {series.map((s, i) => (
                        <span key={s.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                            <span style={{
                                width: '14px', height: '3px', background: MD_SERIES_COLORS[i % MD_SERIES_COLORS.length],
                            }}
                            />
                            {s.name}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
